import { useTranslation } from "react-i18next";
import { useEffect, useState } from 'react';
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import Search from "../../../../components/Search";
import SurveyListTable from "../components/SurveyListTable";
import { SurveyEndRequestDto, SurveyListActions, SurveyListPagination } from "../types";
import { endSurvey, getSurveys } from "../api/surveyAPI";
import { SurveyFilterDto } from "../types";
import { useNavigate } from "react-router-dom";
import { deleteLogicEvent } from "../../events/api/eventAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import AlertMessage from "../../../../components/AlertMessage";
import { SelectedFilter } from "../../../../components/Filters/types";
import { FilterDto, FilterType } from "../../../../types/FilterDto";
import { getCampaignNames } from "../../campaign/api/campaignAPI"; 
import { KeyValuePair } from "../../../../types/KeyValuePair";
import FiltersContainer from "../../../../components/Filters/components/FiltersContainer";
import { setHideFilter } from "../../../../redux/filtersSlice";
import { useQueuedAlerts } from "../../../../hooks/useQueuedAlerts";

export default function SurveyList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const surveyListActions: SurveyListActions = {
        onDelete: async (id: number) => await deleteLogicEvent(id),
        onEnd: async (id: number, body: SurveyEndRequestDto) => await endSurvey(id, body),
        onView: (id: number) => navigate(`/admin/event/${id}`)
    };

    const [searchTerm, setSearchTerm] = useState('');

    const currentPage = useSelector((state: RootState) => state.historical.adminSurveys.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.adminSurveys.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.adminSurveys.searchText);
    const [filters, setFilters] = useState<FilterDto[]>([]);
    const [campaigns, setCampaigns] = useState<KeyValuePair[]>([]);


    const [surveyData, setSurveyData] = useState<SurveyListPagination>({
        page: 1,
        size: 10,
        totalPages: 0,
        totalRecords: 0,
        items: [],
    });

    const { showAlert, alert, isVisible, handleCloseAlert } = useQueuedAlerts({
        defaultDuration: 3000
    });

    const normalizeFilters = (filters: SelectedFilter[]) => {
        const result: Record<string, number[]> = {};

        filters.forEach(f => {
            if (!f.values?.length) return;

            if (f.name === 'campaignId') { 
            result['campaignId'] = f.values.map(v => Number(v));
            }
        });

        return result;
    };

    const fetchSurveys = async ( page = currentPage,size = itemsPerPage, search = searchTerm) => {
        try {
            const normalized = normalizeFilters(appliedFilters);
            const body: SurveyFilterDto = {
                    page,
                    size,
                    search, 
                    filters: normalized
                };
                const data = await getSurveys(body);
                setSurveyData({
                    ...data,
                    items: data.items.map((item: any) => ({
                        id: item.idSurvey,
                        surveyName: item.surveyName,
                        campaign: item.campaignName,
                        status: item.status,
                        adherence: "", // placeholder if not in backend
                        type: item.type,                     
                        startDate: item.startDate,
                        endDate: item.endDate,
                    })),
                    totalPages: Math.ceil(data.totalRecords / (data.size || 1)),
                });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError.message,
                type: 'danger',
                duration: 8000
            });
        }
    };

    const handleSearchInput = () => {
        setSearchTerm(searchText || '');
        dispatch(setCurrentPage({ table: "adminSurveys", page: 1 }));
    }
    const handleClear = () => {
        dispatch(setCurrentPage({ table: "adminSurveys", page: 1 }));
        dispatch(setSearchText({ table: "adminSurveys", text: '' }));
        setSearchTerm('');
    }

    const isFiltersHidden = useSelector((state: RootState) => state.filters.adminSurveys.hideFilter);
    const appliedFilters = useSelector((state: RootState) => state.filters.adminSurveys.selectedFilters);

    const showFilters = () => {
        dispatch(setHideFilter({
            table: 'adminSurveys', hide: !isFiltersHidden
        }));
    }

    const fetchCampaigns = async () => {
        try {
            const campaignOptions = await getCampaignNames();
            
            setCampaigns(campaignOptions.map(c => ({
                key: String(c.id),
                value: c.name
            })));
            
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            showAlert({
                message: fetchError.message,
                type: 'danger',
                duration: 8000
            });
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    useEffect(() => {
        if (campaigns.length > 0) {
        setFilters([
                {
                    name: 'campaignId',
                    description: t("surveyAdmin:headerTableSurvey3") || "Campaign",
                    type: FilterType.MULTIPLE,
                    values: campaigns
                }
            ]);
        }
    }, [campaigns, t]);
        
    useEffect(() => {
        fetchSurveys(currentPage, itemsPerPage, searchTerm);
    }, [currentPage, itemsPerPage, searchTerm, appliedFilters]);


    return (
        <div>
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("surveyAdmin:titleAdminSurveys")}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end gap-2 flex-wrap mt-2 mt-md-0">
                    <Button variant="secondary" style={{ whiteSpace: "nowrap" }}>{t("surveyAdmin:sendSurveyButton")}</Button>
                    <Button
                        variant="secondary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => navigate("/admin/participants/new-participants", { state: { fromSurveyList: true } })}
                    >
                        {t("surveyAdmin:buttonParticipants")}
                    </Button>
                    <Button onClick={() => navigate('/admin/surveyList/new-survey')} variant="primary" style={{ whiteSpace: "nowrap" }}>{t("surveyAdmin:newSurvey")}</Button>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1">
                            <Search
                                searchText={searchText}
                                onChange={(text) => dispatch(setSearchText({ table: "adminSurveys", text: text }))}
                                onSearch={handleSearchInput}
                                onClear={handleClear}
                            />
                        </div>
                        <Button variant="" onClick={showFilters} aria-controls="collapse-filter-id" style={{ border: 0, padding: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '40px' }}
                            />
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={handleCloseAlert}
                    />
                )}
            </Row>
    
            {filters && (
                <FiltersContainer
                    filters={filters}
                    isFilterVisible={!isFiltersHidden}
                    tableKey="adminSurveys"
                    historicalKey="adminSurveys"
                />
            )}
            <SurveyListTable
                surveyData={surveyData}
                totalPages={surveyData.totalPages}
                currentPage={currentPage}
                pageSize={itemsPerPage}
                setMessage={() => { }}
                fetchSurveys={fetchSurveys}
                surveyListActions={surveyListActions}
            />
        </div>
    );
}

